import React, { useState, useEffect } from 'react'
import * as id from '../reportId'
import { Item } from '../../../common/snippets'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import moment from 'moment'
import { Box, Button } from '@mui/material'
import Datepicker from '../../../common/datePicker'
import DropDownOA from '../../../common/dropDownOA'
import { ReportHeading } from '../common/ReportHeading'
import { openReportInNewTab } from '../DispatchReport/DispatchReportUtils'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { urls, xlCol, xlColq } from '../../../common/constants'
import { GetRequest, PostRequest } from '../../../common/api'
import useToken from '../../../helpers/useTokens'
import { useSearchParams } from 'react-router-dom'
import { downloadPDF } from '../../Reports/analysisReport/pdfAutotable'
import { getDecryptedReportParams } from '../../../common/loginDecode'
import { focusElemnt } from '../../../common/helper'
import { DEMO_COMPANY, GT_COMPANY_ID } from '../../../../helper/companyIdhardcode'

const toNumber = (value) => Number(value) || 0
const round2 = (value) => Number(toNumber(value).toFixed(2))

const ALL_BRANCHES = { BranchID: 0, BranchName: 'All Branches' }

const handlingChargesColumns = [
    { header: 'Sr No', exportHead: 'Sr No', field: 'SN', xlCol: xlCol, weight: 18, align: 'left' },
    { header: 'Branch Name', exportHead: 'Branch Name', field: 'BranchName', xlCol: xlCol, isSortIcon: true, weight: 60, align: 'left' },
    { header: 'Amount', exportHead: 'Amount', field: 'Amount', xlCol: xlColq, isSortIcon: true, weight: 40, align: 'right' },
    { header: 'Units', exportHead: 'Units', field: 'Units', xlCol: xlColq, isSortIcon: true, weight: 40, align: 'right' },
    { header: 'Average', exportHead: 'Average', field: 'Average', xlCol: xlColq, isSortIcon: true, weight: 40, align: 'right' },
]

const GT_CENTER_FIELDS = ['BranchName', 'Amount', 'Units', 'Average']
const REPORT_GENERATION_TIME_COMPANY_IDS = [GT_COMPANY_ID, DEMO_COMPANY]

const HandlingChargesReport = () => {
    const setAlert = useSetAtom(alertAtom)
    const setLoader = useSetAtom(globalLoaderAtom)
    const reportName = 'Handling Charges'
    const [fromDate, setFromDate] = useState(moment(new Date()))
    const [toDate, setToDate] = useState(moment(new Date()))
    const [branchData, setBranchData] = useState([ALL_BRANCHES])
    const [selectedBranch, setSelectedBranch] = useState(ALL_BRANCHES)
    const [urlSearchParams] = useSearchParams()
    const searchParams = getDecryptedReportParams(urlSearchParams)

    let { loginData = {} } = useToken()
    let isFromUrl = false
    if (!loginData) {
        loginData = {}
        loginData.intCompanyID = +searchParams.get('C') || 0
        loginData.intUserID = +searchParams.get('U') || 0
        loginData.intBranchID = +searchParams.get('B') || 0
        loginData.strUserName = searchParams.get('UN') || ''
        loginData.strCompanyName = searchParams.get('CN') || ''
        loginData.strBranchName = searchParams.get('BN') || ''
        isFromUrl = true
    }
    const { intCompanyID } = loginData
    const isGT = intCompanyID === GT_COMPANY_ID
    const showReportGenerationTime = REPORT_GENERATION_TIME_COMPANY_IDS.includes(intCompanyID)
    const reportColumns = isGT
        ? handlingChargesColumns.map((col) =>
            GT_CENTER_FIELDS.includes(col.field) ? { ...col, align: 'center' } : col
        )
        : handlingChargesColumns

    const criteriaHeaders = [
        { header: 'From Date', exportHead: 'From Date', field: 'From_Date', textAlign: 'left' },
        { header: 'To Date', exportHead: 'To Date', field: 'To_Date', textAlign: 'left' },
        { header: 'Branch Name', exportHead: 'Branch Name', field: 'Branch_Name', textAlign: 'left' },
        showReportGenerationTime && { header: 'Report Generation Time', exportHead: 'Report Generation Time', field: 'Report_Generation_Time', textAlign: 'left' },
    ].filter(Boolean)

    const buildCriteriaData = (branchName, reportGenerationTime) => [
        {
            dataType: 'entry',
            From_Date: moment(fromDate).format('DD-MM-YYYY'),
            To_Date: moment(toDate).format('DD-MM-YYYY'),
            Branch_Name: branchName || 'All Branches',
            ...(showReportGenerationTime && {
                Report_Generation_Time: reportGenerationTime,
            }),
        },
    ]

    const formatTableData = (table = []) => {
        const totals = { Amount: 0, Units: 0 }
        const rows = table.map((item, index) => {
            const row = {
                SN: index + 1,
                BranchName: item.BranchName || '',
                Amount: toNumber(item.Amount),
                Units: toNumber(item.Units ?? item.Parcel),                
                Average: round2(item.Average ?? item.Avg),
                dataType: 'entry',
            }
            totals.Amount += row.Amount
            totals.Units += row.Units
            return row
        })

        if (rows.length > 0) {
            rows.push({
                SN: 'TOTAL',
                BranchName: '-',
                Amount: round2(totals.Amount),
                Units: round2(totals.Units),
                Average: totals.Units ? round2(totals.Amount / totals.Units) : 0,
                dataType: 'total',
                colSpan: { SN: 2 },
                align: { SN: 'left' },
            })
        }
        return rows
    }

    useEffect(() => {
        focusElemnt(id.fromDate)
    }, [])

    useEffect(() => {
        if (!intCompanyID) return
        PostRequest(urls.getBranchConfigOrEwbillinfo, {
            intUserID: 0,
            intCompanyID,
            intBranchID: 0,
            strBranchName: '',
            strBranchCode: '',
            intStateID: 0,
            intCityID: 0,
            intIsActive: 1,
        })
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Failed to load branches')
                }
                setBranchData([ALL_BRANCHES, ...(res?.Data?.Table || [])])
            })
            .catch((err) => {
                setBranchData([ALL_BRANCHES])
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intCompanyID])

    const handleGo = (isPdf = false) => {
        if (Math.abs(toDate.diff(fromDate, 'days')) > 31) {
            setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
            return
        }
        const branchId = selectedBranch?.BranchID ?? 0
        const branchName = selectedBranch?.BranchName || 'All Branches'
        const params = {
            strDateFrom: moment(fromDate).format('YYYY-MM-DD'),
            strDateTo: moment(toDate).format('YYYY-MM-DD'),
            intCompanyID,
            intBranchID: branchId,
        }
        setLoader(true)
        GetRequest(urls.getHandlingChargesReport, params)
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                const tableData = res?.Data?.table || res?.Data?.Table || []
                if (tableData.length === 0) {
                    throw new Error('No Data Found')
                }
                const formatted = formatTableData(tableData)
                const reportFromDate = showReportGenerationTime ? moment(fromDate).format('DD-MM-YYYY') : ''
                const reportToDate = showReportGenerationTime ? moment(toDate).format('DD-MM-YYYY') : ''
                const reportGenerationTime = showReportGenerationTime
                    ? moment(window.getTime ? window.getTime() : new Date()).format('DD-MM-YYYY, hh:mm A')                    
                    : ''
                const criteriaData = buildCriteriaData(branchName, reportGenerationTime)
                if (isPdf === true) {
                    downloadPDF({
                        reportName: `${reportName} Report`,
                        criteriaHeaders,
                        criteriaData,
                        tableData: formatted,
                        visibleColumns: reportColumns,
                        loginData,
                        showColumnBorders: true,
                    })
                    return
                }
                openReportInNewTab({
                    reportName: `${reportName} Report`,
                    mainHeader: reportColumns,
                    criteriaHeaders,
                    criteriaData,
                    tableData: formatted,
                    isShowManageColumn: false,
                    manageColumnID: '',
                    showColumnBorders: true,
                    isIncreFontSize: true,
                    serialNokey: 'SN',
                    reportFromDate,
                    reportToDate,
                    reportGenerationTime,
                })
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))
    }

    const handleGoKeyDown = (e) => {
        if (e.key === 'Escape') focusElemnt(id.selBranch)
        if (e.key === 'Enter') {
            e.preventDefault()
            handleGo(isFromUrl)
        }
    }

    return (
        <>
            <Item sx={{ padding: '0' }} className="reports">
                {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
                {!isFromUrl && <ReportHeading reportName={reportName} />}
                <Box sx={{ ...sx.reportFlex, justifyContent: 'start', gap: isFromUrl ? '15px' : '10px' }}>
                    <Item sx={sx.itemsxGrow} style={{ width: isFromUrl ? '100%' : '90px' }} className="input--container bookingDatePicker">
                        <Datepicker
                            name={'From_Date'}
                            value={fromDate}
                            label={'From Date'}
                            id={id.fromDate}
                            dateChange={(date) => { setFromDate(date); focusElemnt(id.toDate) }}
                            width="100%"
                        />
                    </Item>
                    <Item
                        sx={sx.itemsxGrow}
                        style={{ width: isFromUrl ? '100%' : '90px' }}
                        className="input--container bookingDatePicker"
                        onKeyDownCapture={(e) => {
                            if (e.key !== 'Escape') return
                            e.preventDefault()
                            e.stopPropagation()
                            focusElemnt(id.fromDate)
                        }}
                    >
                        <Datepicker
                            label={'To Date'}
                            value={toDate}
                            id={id.toDate}
                            dateChange={(date) => { setToDate(date); focusElemnt(id.selBranch) }}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow} style={{ width: isFromUrl ? '100%' : '90px' }}>
                        <DropDownOA
                            id={id.selBranch}
                            label={'Branch Name'}
                            searchPlaceholder={`Search Branch`}
                            defaultvalue={selectedBranch?.BranchName || 'All Branches'}
                            data={branchData || []}
                            dataKey={`BranchName`}
                            idKey={`BranchID`}
                            onSelect={(obj) => setSelectedBranch(obj)}
                            onReset={() => setSelectedBranch(ALL_BRANCHES)}
                            prevId={id.toDate}
                            nextId={id.goButton}
                            width="100%"
                        />
                    </Item>
                    {isFromUrl ?
                        <Button variant="contained" sx={{ width: '100%', height: '40px' }} className="btn btn-primary"
                            onClick={() => handleGo(true)}
                            onKeyDown={handleGoKeyDown}
                            id={id.goButton}
                        >
                            DOWNLOAD PDF
                        </Button> :
                        <Item sx={sx.itemsxGrow} className="disabled-wrapper">
                            <Button id={id.goButton} variant="contained" style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary"
                                onClick={() => handleGo(false)}
                                onKeyDown={handleGoKeyDown}
                            >
                                GO
                            </Button>
                        </Item>
                    }
                </Box>
            </Item>
        </>
    )
}

export default HandlingChargesReport
